import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft,
    Coins,
    Lock,
    Bitcoin,
    Copy,
    Check,
    AlertCircle,
    Loader2,
    CheckCircle2,
    XCircle,
    Wallet,
} from "lucide-react";

function CopyBtn({ value, testId }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            onClick={() => {
                navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }}
            data-testid={testId}
            className="brutal-btn-white text-[10px] py-1.5 px-2 flex-shrink-0"
        >
            {copied ? <Check className="w-3 h-3" strokeWidth={3} /> : <Copy className="w-3 h-3" strokeWidth={2.5} />}
        </button>
    );
}

function CryptoOrderView({ order, onCredited }) {
    const [current, setCurrent] = useState(order);
    useEffect(() => setCurrent(order), [order]);
    useEffect(() => {
        if (current.status !== "PENDING") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/payments/crypto/order/${current.id}`);
                setCurrent(data);
                if (data.status === "CONFIRMED") onCredited && onCredited(data);
            } catch {}
        }, 12000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current.id, current.status]);

    const uriMap = {
        BTC: `bitcoin:${current.address}?amount=${current.amount_str}`,
        ETH: `ethereum:${current.address}?value=${current.smallest_unit}`,
        USDC: `ethereum:${current.address}`,
    };
    const qr = uriMap[current.coin] || current.address;

    if (current.status === "CONFIRMED") {
        return (
            <div data-testid="crypto-order-confirmed" className="brutal-card p-6 bg-vibrantyellow">
                <CheckCircle2 className="w-10 h-10 text-ink mb-2" strokeWidth={2.5} />
                <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">Payment received</h3>
                <p className="font-body text-sm text-ink/80 mt-1">+{current.quantity} credit{current.quantity === 1 ? "" : "s"} added to your balance.</p>
                {current.tx_hash && (
                    <p className="font-body text-[10px] text-ink/60 mt-2 break-all">tx: {current.tx_hash}</p>
                )}
            </div>
        );
    }
    if (current.status === "EXPIRED" || current.status === "FAILED") {
        return (
            <div data-testid="crypto-order-failed" className="brutal-card p-6 bg-vibrantpink/15 border-vibrantpink">
                <XCircle className="w-10 h-10 text-vibrantpink mb-2" strokeWidth={2.5} />
                <h3 className="font-heading font-black text-xl text-ink">Order {current.status.toLowerCase()}</h3>
                <p className="font-body text-sm text-ink/70 mt-1">Create a new order if you'd still like to pay.</p>
            </div>
        );
    }

    const expiresAt = new Date(current.expires_at);
    const remain = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 1000));
    const mins = Math.floor(remain / 60);

    return (
        <div data-testid="crypto-order-pending" className="brutal-card p-5 bg-white space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 text-vibrantorange animate-spin" strokeWidth={2.5} />
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink">Waiting for {current.coin}</span>
                </div>
                <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60 bg-cream border-[2px] border-ink rounded-md px-2 py-0.5">
                    Expires in {mins}m
                </span>
            </div>

            <div className="flex justify-center bg-cream border-[3px] border-ink rounded-2xl p-4">
                <QRCodeSVG value={qr} size={180} level="M" includeMargin={false} />
            </div>

            <div>
                <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 mb-1">Send exactly</div>
                <div className="flex items-center gap-2">
                    <div data-testid="crypto-order-amount" className="font-heading font-black text-2xl text-ink tracking-tight flex-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 break-all">
                        {current.amount_str} {current.coin}
                    </div>
                    <CopyBtn value={current.amount_str} testId="crypto-copy-amount" />
                </div>
                <div className="font-body text-[10px] text-ink/50 mt-1">≈ ${current.amount_usd.toFixed(2)} USD</div>
            </div>

            <div>
                <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 mb-1">To address</div>
                <div className="flex items-center gap-2">
                    <div data-testid="crypto-order-address" className="font-mono text-xs text-ink flex-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 break-all">
                        {current.address}
                    </div>
                    <CopyBtn value={current.address} testId="crypto-copy-address" />
                </div>
            </div>

            <p className="font-body text-[11px] text-ink/60 text-center leading-snug">
                Send the <b>exact</b> amount. The trailing decimals make your transaction identifiable.
                {current.coin === "USDC" ? " Use the Ethereum network (ERC-20)." : ""}
                {" "}Credits land automatically once the first confirmation hits (~10-15 min for BTC, ~30s for ETH/USDC).
            </p>
        </div>
    );
}

export default function BuyCredits() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [credits, setCredits] = useState(null);
    const [cryptoConfig, setCryptoConfig] = useState(null);
    const [qty, setQty] = useState(5);
    const [coin, setCoin] = useState("BTC");
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const isLoggedIn = user && user !== false;

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/payments/i2v/balance");
            setCredits(data.credits);
        } catch {}
    }, [isLoggedIn]);

    useEffect(() => {
        api.get("/payments/crypto/config").then((r) => setCryptoConfig(r.data)).catch(() => {});
        loadBalance();
    }, [loadBalance]);

    const price = parseFloat(cryptoConfig?.price_usd_per_credit || "0.40");
    const total = (price * qty).toFixed(2);

    const createCryptoOrder = async () => {
        setError("");
        setSubmitting(true);
        setOrder(null);
        try {
            const { data } = await api.post("/payments/crypto/order", { coin, quantity: qty });
            setOrder(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="buy-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantyellow border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Wallet className="w-6 h-6 sm:w-7 sm:h-7 text-ink" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Buy <span className="text-vibrantorange">credits</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        ${price.toFixed(2)} per credit · spend on video, music, or bets.
                    </p>
                    <Link to="/cashout" data-testid="buy-credits-cashout-link" className="inline-flex items-center gap-1.5 mt-2 font-heading font-bold text-xs uppercase tracking-widest text-vibrantorange hover:text-ink transition-colors">
                        Cash out to Bitcoin →
                    </Link>
                </div>
                {isLoggedIn && credits != null && (
                    <div data-testid="buy-credits-chip" className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal flex-shrink-0">
                        <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-sm text-ink">{credits}</span>
                    </div>
                )}
            </div>

            {authLoading ? (
                <div className="font-heading font-bold text-ink/60">Loading…</div>
            ) : !isLoggedIn ? (
                <div data-testid="buy-login-gate" className="brutal-card p-8 bg-white">
                    <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to buy credits</h2>
                    <div className="flex flex-wrap gap-3 mt-4">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            ) : (
                <div className="space-y-5">
                    {/* Quantity */}
                    <div className="brutal-card p-5 bg-vibrantyellow">
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink">How many credits?</span>
                            <span data-testid="buy-qty-total" className="font-heading font-black text-2xl text-ink">${total}</span>
                        </div>
                        <div className="flex items-stretch gap-2">
                            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="buy-qty-minus" className="bg-white border-[3px] border-ink rounded-xl w-11 font-heading font-black text-xl shadow-brutal hover:-translate-y-0.5 transition-all">−</button>
                            <input
                                type="number"
                                min={1}
                                value={qty}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (Number.isNaN(v)) return setQty(1);
                                    setQty(Math.max(1, v));
                                }}
                                data-testid="buy-qty-input"
                                className="flex-1 bg-white border-[3px] border-ink rounded-xl font-heading font-black text-xl text-ink text-center shadow-brutal focus:outline-none"
                            />
                            <button type="button" onClick={() => setQty((q) => q + 1)} data-testid="buy-qty-plus" className="bg-white border-[3px] border-ink rounded-xl w-11 font-heading font-black text-xl shadow-brutal hover:-translate-y-0.5 transition-all">+</button>
                        </div>
                        <div className="flex gap-1.5 mt-2" data-testid="buy-qty-presets">
                            {[10, 50, 100, 500, 1000].map((p) => (
                                <button key={p} type="button" onClick={() => setQty(p)} data-testid={`buy-qty-preset-${p}`} className={`flex-1 font-heading font-bold text-xs uppercase tracking-widest border-[2px] border-ink rounded-lg py-1.5 transition-all ${qty === p ? "bg-ink text-white" : "bg-white text-ink hover:bg-cream"}`}>
                                    {p}
                                </button>
                            ))}
                        </div>
                        <p className="font-body text-[11px] text-ink/60 mt-2 text-center">No cap — buy as many credits as you'd like.</p>
                    </div>

                    {/* Crypto panel */}
                    <div data-testid="buy-crypto-panel" className="space-y-4">
                        {!order && (
                            <div className="brutal-card p-5 bg-white">
                                <div className="flex items-center gap-2 mb-3">
                                    <Bitcoin className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} />
                                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink">Pay with crypto</span>
                                </div>
                                <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 mb-2">Choose coin</div>
                                <div className="grid grid-cols-3 gap-2 mb-4" data-testid="buy-coin-group">
                                    {(cryptoConfig?.coins || []).map((c) => (
                                        <button
                                            key={c.coin}
                                            type="button"
                                            onClick={() => setCoin(c.coin)}
                                            data-testid={`buy-coin-${c.coin}`}
                                            className={`brutal-btn py-3 px-2 text-sm ${coin === c.coin ? "bg-ink text-white" : "bg-white text-ink"}`}
                                        >
                                            {c.coin}
                                        </button>
                                    ))}
                                </div>
                                {error && (
                                    <div data-testid="buy-crypto-error" className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-3 mb-3">
                                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                                        <span className="font-body font-semibold text-sm">{error}</span>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={createCryptoOrder}
                                    disabled={submitting || !cryptoConfig?.coins?.length}
                                    className="brutal-btn-primary w-full"
                                    data-testid="buy-crypto-create-order"
                                >
                                    {submitting ? "Generating address…" : `Pay $${total} with ${coin}`}
                                </button>
                            </div>
                        )}
                        {order && (
                            <>
                                <CryptoOrderView order={order} onCredited={loadBalance} />
                                <button
                                    type="button"
                                    onClick={() => setOrder(null)}
                                    data-testid="buy-crypto-cancel"
                                    className="brutal-btn-white w-full text-sm py-2"
                                >
                                    Start a new order
                                </button>
                            </>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2 justify-center pt-2">
                        <button type="button" onClick={() => navigate("/try-video-creator")} className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange">
                            ← Image to Video
                        </button>
                        <span className="text-ink/30">·</span>
                        <button type="button" onClick={() => navigate("/bet-crypto")} className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange">
                            Bet Crypto →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
